import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Sparkle, ArrowRight, Lock } from "@phosphor-icons/react";

export default function Login() {
  const [email, setEmail] = useState("admin@mygenie.in");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      nav("/dashboard");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-[#FF6B00]">
        <div className="absolute inset-0 stripes opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00] via-[#FF6B00] to-[#E66000]" />
        <div className="relative z-10 flex flex-col justify-between p-14 text-white w-full">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/15 backdrop-blur">
              <Sparkle weight="fill" size={22} />
            </div>
            <div>
              <div className="font-display text-xl font-bold">myGenie</div>
              <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Partners</div>
            </div>
          </Link>

          <div className="space-y-8 max-w-md">
            <h1 className="font-display text-5xl font-bold leading-[1.05]">
              Onboard sub-merchants in seconds via Razorpay OAuth.
            </h1>
            <p className="text-base text-white/85 leading-relaxed">
              Build, test and ship a partner integration that mirrors Razorpay's production OAuth flow — applications, scoped tokens, refresh, revoke, webhooks and audit, all from one console.
            </p>
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-white" />Sandbox safe</div>
              <div className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-white" />Live mode ready</div>
              <div className="flex items-center gap-2"><div className="h-1.5 w-1.5 rounded-full bg-white" />Webhook driven</div>
            </div>
          </div>

          <div className="text-xs text-white/70">© myGenie. Mirrors Razorpay's OAuth spec for partner integrations.</div>
        </div>
      </div>

      {/* Form */}
      <div className="flex w-full lg:w-1/2 items-center justify-center px-6 py-12 bg-white grid-bg">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#FF6B00]">
              <Sparkle weight="fill" size={16} className="text-white" />
            </div>
            <span className="font-display text-lg font-bold">myGenie Partners</span>
          </div>

          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-[#FFF3E0] px-2.5 py-1 text-[11px] font-medium text-[#FF6B00]">
            <Lock size={12} weight="bold" /> Partner console
          </div>
          <h2 className="font-display text-3xl font-bold text-[#0F172A]">Sign in to your workspace</h2>
          <p className="mt-2 text-sm text-slate-500">Manage applications, sub-merchants, tokens and webhooks.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="login-email-input"
                className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15"
              />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Password</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                data-testid="login-password-input"
                className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B00] focus:ring-2 focus:ring-[#FF6B00]/15"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="btn-primary inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-60"
            >
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={14} weight="bold" /></>)}
            </button>
          </form>

          <div className="mt-6 text-sm text-slate-500">
            New partner?{" "}
            <Link to="/register" className="font-medium text-[#FF6B00] hover:underline" data-testid="goto-register">
              Create an account
            </Link>
          </div>

          <div className="mt-10 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <span className="font-semibold text-slate-800">Demo:</span> admin@mygenie.in / admin123
          </div>
        </div>
      </div>
    </div>
  );
}
